import { useCallback, useId, useRef, useState } from "react";
import { Box, ButtonBase } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { colorPalette } from "../../config/colorPalette";
import List from "../List/List";
import {
  controlSx,
  getStatus,
  labelSx,
  messageSx,
  textSx,
} from "../_shared/fieldStyles";
import { useDismiss } from "../_shared/useDismiss";

/**
 * Select
 *
 * A dropdown field. The open panel is the List component (single select, radios).
 *
 * Props:
 * - label: text above the field
 * - options: [{ label, value, disabled? }]
 * - value: selected value
 * - onChange: (value) => void, called when an option is picked (the panel then closes)
 * - placeholder: shown when nothing is selected
 * - error: true, or a message string shown under the field (red state)
 * - success: green state
 * - disabled
 * - id: control id (generated when omitted)
 * - sx: extra styles for the whole field, merged last
 */
const Select = ({
  label,
  options = [],
  value,
  onChange,
  placeholder = "Select",
  error,
  success = false,
  disabled = false,
  id,
  sx: sxOverride = {},
}) => {
  const generatedId = useId();
  const controlId = id || generatedId;
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const status = getStatus({ error, success, disabled });
  const message = typeof error === "string" ? error : null;
  const selected = options.find((option) => option.value === value);
  const Chevron = open ? KeyboardArrowUpIcon : KeyboardArrowDownIcon;

  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, rootRef);

  return (
    <Box
      ref={rootRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        ...sxOverride,
      }}
    >
      {label && (
        <Box component="span" id={`${controlId}-label`} sx={labelSx(disabled)}>
          {label}
        </Box>
      )}
      <Box sx={{ position: "relative" }}>
        <ButtonBase
          id={controlId}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby={
            label ? `${controlId}-label ${controlId}` : undefined
          }
          onClick={() => setOpen((current) => !current)}
          sx={{
            ...controlSx(status, { focusWithin: false }),
            ...textSx,
            width: "100%",
            justifyContent: "space-between",
            textAlign: "left",
            color: disabled
              ? colorPalette.mercuryAlpha[28]
              : selected
                ? colorPalette.mercury[950]
                : colorPalette.mercuryAlpha[65],
            "&.Mui-focusVisible, &:focus-visible": {
              borderColor: colorPalette.brand[700],
            },
          }}
        >
          <span>{selected ? selected.label : placeholder}</span>
          <Chevron
            aria-hidden="true"
            sx={{
              fontSize: 20,
              color: disabled
                ? colorPalette.mercuryAlpha[28]
                : colorPalette.mercuryAlpha[65],
            }}
          />
        </ButtonBase>
        {open && (
          <Box
            sx={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              zIndex: 10,
            }}
          >
            <List
              items={options}
              value={value}
              onChange={(next) => {
                onChange?.(next);
                setOpen(false);
              }}
            />
          </Box>
        )}
      </Box>
      {message && (
        <Box role="alert" sx={messageSx}>
          {message}
        </Box>
      )}
    </Box>
  );
};

export default Select;
